import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertCircle, TrendingUp, Target, Activity } from 'lucide-react';
import apiCoverageService from './services/apiCoverageService';

export default function ApiCoveragePage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedSwagger, setSelectedSwagger] = useState('adm');
  const [swaggerFilter, setSwaggerFilter] = useState('');
  const [detailsFilter, setDetailsFilter] = useState('');
  const [swaggerSort, setSwaggerSort] = useState({ key: 'feature', direction: 'asc' });
  const [detailsSort, setDetailsSort] = useState({ key: 'endpoint', direction: 'asc' });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const coverageData = await apiCoverageService.loadCoverageData();
        setData(coverageData);
      } catch (err) {
        console.error('Error loading coverage data:', err);
        setError('Unable to load coverage data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const toggleSort = (setSort, key) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortRows = (rows, sortConfig) => {
    const sortedRows = [...rows];
    sortedRows.sort((left, right) => {
      const leftValue = left[sortConfig.key];
      const rightValue = right[sortConfig.key];

      const leftNumber = Number(leftValue);
      const rightNumber = Number(rightValue);
      const areNumbers = Number.isFinite(leftNumber) && Number.isFinite(rightNumber);

      let comparison = 0;
      if (areNumbers) {
        comparison = leftNumber - rightNumber;
      } else {
        const leftText = (leftValue ?? '').toString().toLowerCase();
        const rightText = (rightValue ?? '').toString().toLowerCase();
        comparison = leftText.localeCompare(rightText, 'fr');
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return sortedRows;
  };

  const sortIndicator = (sortConfig, key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const swaggerRows = useMemo(() => {
    if (!data?.swaggers?.[selectedSwagger]?.tags) return [];
    const query = swaggerFilter.trim().toLowerCase();
    const filtered = data.swaggers[selectedSwagger].tags.filter((row) => {
      if (!query) return true;
      return Object.values(row).some((value) => (value ?? '').toString().toLowerCase().includes(query));
    });
    return sortRows(filtered, swaggerSort);
  }, [data, selectedSwagger, swaggerFilter, swaggerSort]);

  const detailRows = useMemo(() => {
    if (!data?.swaggers?.[selectedSwagger]?.details) return [];
    const query = detailsFilter.trim().toLowerCase();
    const filtered = data.swaggers[selectedSwagger].details.filter((row) => {
      if (!query) return true;
      return Object.values(row).some((value) => (value ?? '').toString().toLowerCase().includes(query));
    });
    return sortRows(filtered, detailsSort);
  }, [data, selectedSwagger, detailsFilter, detailsSort]);

  const getToolLabel = (tool) => {
    if (tool === 'Robot') return 'Robot Framework';
    return tool;
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading coverage data...</div>;
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-800">{error}</h3>
            <p className="text-sm text-red-600 mt-1">Make sure api-coverage.xlsx is available in the public path.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-4">
            <Target className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">API Coverage</h1>
          </div>
          <p className="text-gray-600">Complete coverage analysis for APIs (ReadyAPI + Robot Framework)</p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" role="tablist">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-1 border-b-2 font-medium ${
                  activeTab === 'overview'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-700 hover:text-blue-600'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('swagger')}
                className={`py-4 px-1 border-b-2 font-medium ${
                  activeTab === 'swagger'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-700 hover:text-blue-600'
                }`}
              >
                By Domain
              </button>
              <button
                onClick={() => setActiveTab('details')}
                className={`py-4 px-1 border-b-2 font-medium ${
                  activeTab === 'details'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-700 hover:text-blue-600'
                }`}
              >
                Endpoint Details
              </button>
            </nav>
          </div>
        </div>

        {/* TAB 1: VUE D'ENSEMBLE */}
        {activeTab === 'overview' && data && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-3xl font-bold text-blue-600">
                  {data.sourceComparison.find(s => s.tool === 'Combined')?.coverage.toFixed(1) || '0'}%
                </div>
                <div className="text-sm text-gray-600 mt-2">Total Coverage</div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-3xl font-bold text-green-600">
                  {data.sourceComparison.find(s => s.tool === 'Combined')?.testedEndpoints || 0}
                </div>
                <div className="text-sm text-gray-600 mt-2">Tested Endpoints</div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-3xl font-bold text-indigo-600">
                  {data.sourceComparison.find(s => s.tool === 'Combined')?.totalEndpoints || data.globalSwagger.reduce((sum, s) => sum + (s.totalEndpoints || 0), 0)}
                </div>
                <div className="text-sm text-gray-600 mt-2">Total Endpoints</div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-3xl font-bold text-purple-600">
                  {data.globalSwagger.length}
                </div>
                <div className="text-sm text-gray-600 mt-2">Domains</div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-3xl font-bold text-orange-600">
                  {data.globalSwaggerTag.length}
                </div>
                <div className="text-sm text-gray-600 mt-2">Features</div>
              </div>
            </div>

            {/* Context */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Context</h2>
              <div className="text-sm text-gray-700 grid grid-cols-1 md:grid-cols-4 gap-3">
                <div><span className="font-medium">Source:</span> {data.metadata?.source || 'api-coverage.xlsx'}</div>
                <div><span className="font-medium">Scope:</span> {data.metadata?.scopeVersion || 'develop'}</div>
                <div><span className="font-medium">Last update:</span> {data.metadata?.lastUpdated ? new Date(data.metadata.lastUpdated).toLocaleString('en-GB') : 'N/A'}</div>
                <div><span className="font-medium">Type:</span> {data.metadata?.isSnapshot ? 'Static snapshot' : 'Live'}</div>
              </div>
            </div>

            {/* 1. Coverage by Domain */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                Coverage by Domain
              </h2>
              <ResponsiveContainer width="100%" height={350}>
                <RadarChart data={data.globalSwagger}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="swagger" />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} />
                  <Radar name="v24.3" dataKey="coverage_243" stroke="#dc2626" fill="#dc2626" fillOpacity={0.20} />
                  <Radar name="v25.2" dataKey="coverage_252" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.20} />
                  <Radar name="v26.1" dataKey="coverage_261" stroke="#16a34a" fill="#16a34a" fillOpacity={0.25} />
                  <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* 2. Evolution par version */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Evolution v24.3 → v25.2 → v26.1</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-700 mb-3">Call volume</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data.globalSwagger}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="swagger" interval={0} angle={-20} textAnchor="end" height={60} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="calls_243" fill="#dc2626" name="v24.3" />
                      <Bar dataKey="calls_252" fill="#f59e0b" name="v25.2" />
                      <Bar dataKey="calls_261" fill="#16a34a" name="v26.1" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h3 className="font-medium text-gray-700 mb-3">Coverage (%)</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data.globalSwagger}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="swagger" interval={0} angle={-20} textAnchor="end" height={60} padding={{ left: 56, right: 24 }} dx={10} />
                      <YAxis domain={[0, 100]} />
                      <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
                      <Legend />
                      <Bar dataKey="coverage_243" fill="#dc2626" name="v24.3" />
                      <Bar dataKey="coverage_252" fill="#f59e0b" name="v25.2" />
                      <Bar dataKey="coverage_261" fill="#16a34a" name="v26.1" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* 3. Coverage by Tool */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                Coverage by Tool
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.sourceComparison}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="tool" tickFormatter={getToolLabel} />
                    <YAxis domain={[0, 100]} />
                    <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                    <Bar dataKey="coverage" fill="#3b82f6" name="Coverage %" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="space-y-4">
                  {data.sourceComparison.map((item, idx) => (
                    <div key={idx} className="border rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900">{getToolLabel(item.tool)}</h3>
                      <div className="mt-2 space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Coverage</span>
                          <span className="font-medium text-blue-600">{item.coverage.toFixed(2)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tested endpoints</span>
                          <span className="font-medium">{item.testedEndpoints}/{item.totalEndpoints}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total calls</span>
                          <span className="font-medium">{item.totalCalls}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Not tested</span>
                          <span className="font-medium text-orange-600">{item.notTestedEndpoints}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 4. Coverage by Feature (Global) */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Coverage by Feature (Global)</h2>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {[...data.globalSwaggerTag].sort((a, b) => b.coverage - a.coverage).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <div className="w-80 text-sm font-medium text-gray-700 break-words">{item.swagger} - {item.tag}</div>
                    <div className="flex-1 max-w-sm">
                      <div className="bg-gray-200 rounded-full h-6 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-blue-400 to-blue-600 h-full rounded-full transition-all"
                          style={{ width: `${Math.min(item.coverage, 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex gap-4 text-xs">
                      <span className="font-semibold text-blue-600 w-12 text-right">{item.coverage.toFixed(1)}%</span>
                      <span className="text-gray-600 w-16">{item.coveredEndpoints}/{item.totalEndpoints}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: BY DOMAIN */}
        {activeTab === 'swagger' && data && (
          <div className="space-y-6">
            {/* Domain selector */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Select a domain</h2>
              <div className="flex flex-wrap gap-2">
                {['adm', 'dp', 'env', 'exp', 'kb', 'agentic', 'osint'].map((swagger) => (
                  <button
                    key={swagger}
                    onClick={() => setSelectedSwagger(swagger)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedSwagger === swagger
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {swagger.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Selected domain details */}
            {data.swaggers[selectedSwagger] && (
              <div className="space-y-6">
                {/* Stats table */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-6 py-4 border-b bg-gray-50">
                    <h3 className="font-semibold text-gray-900">Feature stats - {selectedSwagger.toUpperCase()}</h3>
                    <p className="text-sm text-gray-600 mt-1">{swaggerRows.length} result(s)</p>
                    <div className="mt-3">
                      <input
                        type="text"
                        value={swaggerFilter}
                        onChange={(e) => setSwaggerFilter(e.target.value)}
                        placeholder="Filter (feature, calls, coverage...)"
                        className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort(setSwaggerSort, 'feature')}>Feature {sortIndicator(swaggerSort, 'feature')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort(setSwaggerSort, 'totalEndpoints')}>Total Endpoints {sortIndicator(swaggerSort, 'totalEndpoints')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort(setSwaggerSort, 'testedEndpoints')}>Tested Endpoints {sortIndicator(swaggerSort, 'testedEndpoints')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort(setSwaggerSort, 'coverage')}>Coverage {sortIndicator(swaggerSort, 'coverage')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort(setSwaggerSort, 'totalCalls')}>Total Calls {sortIndicator(swaggerSort, 'totalCalls')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort(setSwaggerSort, 'readyApiCalls')}>ReadyAPI {sortIndicator(swaggerSort, 'readyApiCalls')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort(setSwaggerSort, 'robotframeworkCalls')}>Robot Framework {sortIndicator(swaggerSort, 'robotframeworkCalls')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {swaggerRows.map((tag, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-6 py-3 text-sm text-gray-900 font-medium">{tag.feature}</td>
                            <td className="px-6 py-3 text-sm text-gray-600">{tag.totalEndpoints}</td>
                            <td className="px-6 py-3 text-sm text-gray-600">{tag.testedEndpoints}</td>
                            <td className="px-6 py-3 text-sm">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                tag.coverage >= 80 ? 'bg-green-100 text-green-800' :
                                tag.coverage >= 50 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {tag.coverage.toFixed(1)}%
                              </span>
                            </td>
                            <td className="px-6 py-3 text-sm text-gray-600">{tag.totalCalls}</td>
                            <td className="px-6 py-3 text-sm text-gray-600">{tag.readyApiCalls}</td>
                            <td className="px-6 py-3 text-sm text-gray-600">{tag.robotframeworkCalls}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 3: ENDPOINT DETAILS */}
        {activeTab === 'details' && data && (
          <div className="space-y-6">
            {/* Domain selector */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Select a domain for endpoint details</h2>
              <div className="flex flex-wrap gap-2">
                {['adm', 'dp', 'env', 'exp', 'kb', 'agentic', 'osint'].map((swagger) => (
                  <button
                    key={swagger}
                    onClick={() => setSelectedSwagger(swagger)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedSwagger === swagger
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {swagger.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Endpoint details */}
            {data.swaggers[selectedSwagger] && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b bg-gray-50">
                  <h3 className="font-semibold text-gray-900">Endpoint details - {selectedSwagger.toUpperCase()}</h3>
                  <p className="text-sm text-gray-600 mt-1">{detailRows.length} endpoint(s)</p>
                  <div className="mt-3">
                    <input
                      type="text"
                      value={detailsFilter}
                      onChange={(e) => setDetailsFilter(e.target.value)}
                      placeholder="Filter (endpoint, feature, status, calls...)"
                      className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b sticky top-0">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort(setDetailsSort, 'endpoint')}>Endpoint {sortIndicator(detailsSort, 'endpoint')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort(setDetailsSort, 'feature')}>Feature {sortIndicator(detailsSort, 'feature')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort(setDetailsSort, 'isCovered')}>Status {sortIndicator(detailsSort, 'isCovered')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort(setDetailsSort, 'totalCalls')}>Total Calls {sortIndicator(detailsSort, 'totalCalls')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort(setDetailsSort, 'readyApiCalls')}>ReadyAPI {sortIndicator(detailsSort, 'readyApiCalls')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort(setDetailsSort, 'robotframeworkCalls')}>Robot Framework {sortIndicator(detailsSort, 'robotframeworkCalls')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {detailRows.map((endpoint, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-6 py-3 font-mono text-xs text-gray-900 max-w-xs truncate">{endpoint.endpoint}</td>
                          <td className="px-6 py-3 text-gray-600">{endpoint.feature}</td>
                          <td className="px-6 py-3">
                            {endpoint.isCovered ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">✓ Covered</span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">✗ Not covered</span>
                            )}
                          </td>
                          <td className="px-6 py-3 font-medium">{endpoint.totalCalls}</td>
                          <td className="px-6 py-3 text-blue-600">{endpoint.readyApiCalls}</td>
                          <td className="px-6 py-3 text-green-600">{endpoint.robotframeworkCalls}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
